<template lang="pug">
div
  TxtRefinedAI(
    v-if="labelPosition === 'animated'"
    ref="inputComponent"
    v-bind="inputProps"
    @update:modelValue="updateSelf"
    @blur="onBlur"
    @focus="onFocus")
  TxtRefined(
    v-else
    ref="inputComponent"
    v-bind="inputProps"
    @update:modelValue="updateSelf"
    @blur="onBlur"
    @focus="onFocus")
</template>

<script setup lang="ts">
/**
 * Short for `<input type="text" />` with additional features.
 * Set `place` event handler in the attributes and the component extend itself
 * with Europe restricted **Places** autocomplete
 * Its value should be a function which parameter receive the
 * [Google Places](https://developers.google.com/places/web-service/details#PlaceDetailsResponses)
 * object.
 */

import { LOCALE } from "~/constants/locale"

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ""
  },
  /** Add a green check icon */
  check: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: ""
  },
  labelPosition: {
    type: String,
    default: "top",
    validator: value => ["top", "left", "animated", "border"].includes(value)
  },
  labelBg: {
    type: String,
    default: ""
  },
  error: {
    type: String,
    default: ""
  },
  /** Hide error message - just red placeholder and border */
  noErrorMessage: {
    type: Boolean,
    default: false
  },
  /** Standard input types */
  type: {
    type: String,
    default: "text"
  },
  size: {
    type: String,
    default: "xl",
    validator: value => ["xs", "s", "m", "l", "xl"].includes(value)
  },
  inputId: {
    type: String,
    default: ""
  },
  placeholder: {
    type: String,
    default: ""
  },
  disabled: {
    type: Boolean,
    default: false
  },
  checkAndDisable: {
    type: Boolean,
    default: false
  },
  focused: {
    type: Boolean,
    default: false
  },
  autocomplete: {
    type: String,
    default: "off",
    required: false
  },
  /** Icon shortcut */
  iconLeft: {
    type: String,
    default: ""
  },
  /** Icon shortcut */
  iconRight: {
    type: String,
    default: ""
  },
  /** Icon size */
  // eslint-disable-next-line vue/require-default-prop
  iconSize: {
    type: Number,
    required: false
  },
  /** Icon colour */
  // eslint-disable-next-line vue/require-default-prop
  iconColor: {
    type: String,
    required: false
  },
  min: {
    type: Number,
    required: false,
    default: null
  },
  max: {
    type: Number,
    required: false,
    default: null
  },
  showArrows: {
    type: Boolean,
    default: false
  },
  border: {
    type: Boolean,
    default: true
  },
  backgroundColor: {
    type: String,
    default: ""
  },
  iconPadding: {
    type: Number,
    required: false,
    default: null
  },
  locale: {
    type: String,
    default: LOCALE.UK
  },
  customClasses: {
    type: String
  }
})

const emit = defineEmits(["place", "focus", "blur", "onFocus", "onBlur"])

const inputValue = ref("")
const placeDirty = ref(false)
const guessPlaceTimeout = ref(null)
const geocoder = ref(null)
const placesAutocomplete = ref(null)
const inputComponent = ref(null)

const inputProps = computed(() => ({
  modelValue: props.modelValue,
  check: props.check,
  label: props.label,
  labelPosition: props.labelPosition,
  labelBg: props.labelBg,
  error: props.error,
  noErrorMessage: props.noErrorMessage,
  type: props.type,
  size: props.size,
  inputId: props.inputId,
  placeholder: props.placeholder,
  checkAndDisable: props.checkAndDisable,
  focused: props.focused,
  autocomplete: props.autocomplete,
  iconLeft: props.iconLeft,
  iconRight: props.iconRight,
  iconSize: props.iconSize,
  iconColor: props.iconColor,
  min: props.min,
  max: props.max,
  showArrows: props.showArrows,
  border: props.border,
  backgroundColor: props.backgroundColor,
  iconPadding: props.iconPadding,
  customClasses: props.customClasses
}))

const getAutoCompleteBoundaries = (locale: string) => {
  if (locale === LOCALE.SPAIN) {
    return new google.maps.LatLngBounds(
      { lat: 34.761126, lng: -10.294783 },
      { lat: 44.285538, lng: 4.750203 }
    )
  }
  return new google.maps.LatLngBounds(
    { lat: 49.686402, lng: -7.678393 },
    { lat: 59.241507, lng: 1.73144 }
  )
}

const initMap = () => {
  if (!inputComponent.value.input) return

  placesAutocomplete.value = new google.maps.places.Autocomplete(inputComponent.value.input, {
    types: ["geocode", "establishment"],
    // UK
    bounds: getAutoCompleteBoundaries(props.locale)
  })
  placesAutocomplete.value.setFields([
    "geometry",
    "formatted_address",
    "place_id",
    "address_components"
  ])
  placesAutocomplete.value.addListener("place_changed", () => {
    // Address was selected, no need to guess.
    clearGuessPlaceTimeout()
    placeDirty.value = false

    // const place = Object.freeze(autocomplete.getPlace())
    const place = placesAutocomplete.value.getPlace()
    inputValue.value = place.formatted_address

    if (!inputValue.value) {
      clearPlace()
    } else {
      emit("place", place, inputValue.value)
    }
  })

  geocoder.value = new google.maps.Geocoder()
}

const updateSelf = (value, event) => {
  if (value === "") {
    clearPlace()
    return
  } else if (value.length >= 2 && !placesAutocomplete.value) {
    if (typeof google !== "undefined") {
      initMap()
    } else {
      const callback = () => {
        if (typeof google !== "undefined") {
          window.removeEventListener("maps-module:loaded", callback)
        }
        initMap()
      }
      window.addEventListener("maps-module:loaded", callback)
    }
  }

  if (event && event.data !== undefined) {
    // In this case the user fired an InputEvent, type = insertText, data = "X", composed = true
    // Where X can be a letter typed by the user and target.value is the full inserted value
    // Should consider that data = "" when the user removes a char from the input field
    placeDirty.value = true
  } else {
    // In this case the user fired a CustomEvent, type = undefined, data = undefined, composed = false
    // Where target.value is the full inserted value can be autocomplete or AutoFill
    // Of a Contact Address on Android / iPhone
    guessPlace(value)
  }
}

const onFocus = () => {
  emit("focus")
}

const onBlur = () => {
  emit("blur")
  // If the user leaves the input without selecting an address,
  // but the dropdown is visible, try to use the first result.
  if (placeDirty.value) {
    let address = ""
    const item = document.querySelector(".pac-container .pac-item")

    if (item !== null) {
      address = Array.from(item.children, node => node.textContent)
        .filter(Boolean)
        .join(" ")
        .trim()

      // Autocomplete leave hidden .pac-container elements in dom,
      // remove it so it doesn't bother in the future.
      item.remove()
    }

    // We need to assign this value outside the timeout
    const searchValue = inputComponent.value?.input?.value

    // Blur event triggering faster than `place_changed`,
    // wait a little bit, maybe an address was selected.
    guessPlaceTimeout.value = setTimeout(() => {
      guessPlace(address, searchValue)
    }, 500)
  }
}

const guessPlace = (address: string, searchValue?: string | null) => {
  searchValue = searchValue || address
  clearGuessPlaceTimeout()

  if (address !== "") {
    geocoder.value.geocode({ address }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results.length > 0) {
        // const place = Object.freeze(results[0])
        const place = results[0]
        inputValue.value = place.formatted_address
        emit("place", place, address, searchValue, true)
        placeDirty.value = false
      }

      if (placeDirty.value) {
        clearPlace()
      }
    })
  } else {
    clearPlace()
  }
}

const clearPlace = () => {
  inputValue.value = ""
  emit("place", null)
  placeDirty.value = false
}

const clearGuessPlaceTimeout = () => {
  if (guessPlaceTimeout.value !== null) {
    clearTimeout(guessPlaceTimeout.value)
    guessPlaceTimeout.value = null
  }
}

onMounted(() => {
  const callback = () => {
    if (typeof google !== "undefined") {
      window.removeEventListener("maps-module:loaded", callback)
    }
    initMap()
  }
  window.addEventListener("maps-module:loaded", callback)
})

onBeforeUnmount(() => {
  clearGuessPlaceTimeout()
})
</script>

<style lang="css" module>
:global {
  .pac-container {
    @apply border-blue-primary;
    color: #6e6e6e;
    position: absolute;
    top: 55px;
    width: 100%;
    background-color: #fff;
    box-shadow: none;
    border-width: 0 1px 1px;
    box-sizing: content-box;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    .ddloc {
      position: relative;
      padding: 10px 12px 10px 30px;
      font-size: 15px;
      cursor: pointer;
      font-weight: normal;
      .icon-location-pin-2 {
        font-size: 20px;
        color: #c7c7c7;
        position: absolute;
        top: 10px;
        left: 6px;
      }
      &:hover {
        background-color: #f5f5f5;
      }
      &.selected {
        background-color: #f5f5f5;
      }
    }
    .gMapLogo {
      background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='103' height='16' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e%3cimage width='103' height='16' xlink:href='data:image/png;base64,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' fill='none' fill-rule='evenodd'/%3e%3c/svg%3e");
      background-position: bottom left;
      height: 15px;
      margin: 0 0 5px 12px;
    }

    .pac-item {
      width: 100% !important;
      height: 100%;
      min-height: 32px;
      z-index: 100;
      display: inline-block;
      white-space: normal;
      padding: 16px 14px 16px 42px;
      font-size: 16px;
      border: none;
      line-height: 24px;
      overflow: visible;

      &:hover {
        background-color: #efefef;
      }

      .pac-icon {
        background-image: url("data:image/svg+xml;charset=UTF-8,%3c?xml version='1.0' ?%3e%3csvg viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e%3cdefs%3e%3cstyle%3e.cls-1%7bfill:url(%23linear-gradient);%7d%3c/style%3e%3clinearGradient gradientUnits='userSpaceOnUse' id='linear-gradient' x1='12.22' x2='37.03' y1='24' y2='24'%3e%3cstop offset='0' stop-color='%23d8d8d8'/%3e%3cstop offset='1' stop-color='%23d8d8d8'/%3e%3c/linearGradient%3e%3c/defs%3e%3ctitle/%3e%3cg id='icons'%3e%3cg data-name='Layer 3' id='Layer_3'%3e%3cpath class='cls-1' d='M18.72,8.31a10.79,10.79,0,0,0-5.17,5.24A12.74,12.74,0,0,0,14.7,26.81l9.93,14.25,9.92-14.25A12.33,12.33,0,0,0,37,19.36C37,10.6,28,4,18.72,8.31Zm6.08,15.2A4.51,4.51,0,1,1,29.3,19,4.51,4.51,0,0,1,24.8,23.51Z'/%3e%3c/g%3e%3c/g%3e%3c/svg%3e");
        background-size: 30px auto;
        background-position: right;
        height: 22px;
        width: 24px;
        margin: 2px 4px 0 -28px;
      }

      .pac-item-query {
        font-size: 14px;

        .pac-matched {
          font-size: 14px;
        }
      }
    }

    &::after {
      height: 28px;
      background-position: 16px;
      background-size: 100px;
      filter: grayscale(50%) opacity(40%);
    }
  }
}
</style>
